import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Save, Loader2, ChevronLeft, Image as ImageIcon } from "lucide-react";
import { useContent } from "@/contexts/ContentContext";
import { SECTION_SCHEMAS } from "./schemas";
import { Icon } from "@/lib/icons";

// ----- path utils for nested keys like "social.facebook" -----
const get = (obj, path) => path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);
const set = (obj, path, value) => {
  const parts = path.split(".");
  const next = { ...obj };
  let cur = next;
  for (let i = 0; i < parts.length - 1; i++) {
    cur[parts[i]] = { ...(cur[parts[i]] || {}) };
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
  return next;
};

export default function SectionEditor() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { content, save, loading } = useContent();
  const schema = SECTION_SCHEMAS[key];

  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  // Initialize draft from content
  useEffect(() => {
    if (!content) return;
    if (schema?.pathRoot === "site") {
      setDraft({ ...(content.site || {}) });
    } else {
      setDraft({ ...(content.sections?.[key] || {}) });
    }
  }, [content, key, schema]);

  const dirty = useMemo(() => {
    if (!content || !draft) return false;
    const original = schema?.pathRoot === "site" ? content.site : content.sections?.[key];
    return JSON.stringify(original || {}) !== JSON.stringify(draft);
  }, [content, draft, key, schema]);

  if (!schema) {
    return (
      <div className="p-10">
        <h2 className="font-display text-2xl font-bold">Unknown section</h2>
        <button onClick={() => navigate("/admin/cms")} className="mt-4 btn-outline-gold !py-2 !px-4 !text-xs">Back</button>
      </div>
    );
  }

  if (loading || !content || !draft) {
    return <div className="p-10 text-white/60">Loading…</div>;
  }

  const updateField = (path, value) => setDraft((d) => set(d, path, value));

  const onSave = async () => {
    setSaving(true);
    try {
      let next;
      if (schema.pathRoot === "site") {
        next = { ...content, site: draft };
      } else {
        next = { ...content, sections: { ...content.sections, [key]: draft } };
      }
      await save(next);
      toast.success("Saved");
    } catch (e) {
      toast.error("Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  // ---------- List operations ----------
  const list = schema.list;
  const items = list ? draft[list.key] || [] : [];

  const addItem = () => {
    setDraft((d) => ({ ...d, [list.key]: [...(d[list.key] || []), list.defaultItem()] }));
  };
  const removeItem = (idx) => {
    if (!window.confirm("Remove this item?")) return;
    setDraft((d) => ({ ...d, [list.key]: d[list.key].filter((_, i) => i !== idx) }));
  };
  const moveItem = (idx, dir) => {
    const arr = [...(draft[list.key] || [])];
    const ni = idx + dir;
    if (ni < 0 || ni >= arr.length) return;
    [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
    setDraft((d) => ({ ...d, [list.key]: arr }));
  };
  const updateItem = (idx, fieldKey, value) => {
    setDraft((d) => {
      const arr = [...(d[list.key] || [])];
      arr[idx] = { ...arr[idx], [fieldKey]: value };
      return { ...d, [list.key]: arr };
    });
  };
  const toggleItem = (idx) => {
    setDraft((d) => {
      const arr = [...(d[list.key] || [])];
      arr[idx] = { ...arr[idx], enabled: arr[idx].enabled === false ? true : false };
      return { ...d, [list.key]: arr };
    });
  };

  return (
    <div className="p-6 sm:p-10 max-w-5xl">
      {/* Header */}
      <button
        onClick={() => navigate("/admin/cms")}
        className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-[#D4AF37] mb-5"
      >
        <ChevronLeft className="w-3.5 h-3.5" /> Back to dashboard
      </button>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-2">{schema.label}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Edit {schema.label}</h1>
          <p className="mt-2 text-white/60 text-sm max-w-2xl">{schema.description}</p>
        </div>

        <div className="flex items-center gap-3">
          {schema.hasToggle !== false && schema.pathRoot !== "site" && (
            <button
              onClick={() => updateField("enabled", !(draft.enabled !== false))}
              data-testid="toggle-section-visibility"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all border ${
                draft.enabled !== false
                  ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                  : "bg-white/5 text-white/60 border-white/15"
              }`}
            >
              {draft.enabled !== false ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {draft.enabled !== false ? "Visible" : "Hidden"}
            </button>
          )}
          <button
            onClick={onSave}
            disabled={saving || !dirty}
            data-testid="save-section"
            className="btn-gold !py-3 !px-5 !text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* Main fields */}
      <div className="bg-[#1A1A1A]/70 border border-[#D4AF37]/20 rounded-3xl p-6 sm:p-8 mb-8 space-y-5">
        <h2 className="font-display text-base uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">Content</h2>
        {schema.fields.map((f) => (
          <FieldInput
            key={f.key}
            field={f}
            value={get(draft, f.key)}
            onChange={(v) => updateField(f.key, v)}
          />
        ))}
      </div>

      {/* List items */}
      {list && (
        <div className="bg-[#1A1A1A]/70 border border-[#D4AF37]/20 rounded-3xl p-6 sm:p-8" data-testid="section-list">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-base uppercase tracking-[0.3em] text-[#D4AF37] font-bold">{list.label}</h2>
            <button
              onClick={addItem}
              data-testid="add-list-item"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold bg-[#D4AF37] text-[#0A0A0A] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all"
            >
              <Plus className="w-3.5 h-3.5" /> Add item
            </button>
          </div>

          {items.length === 0 ? (
            <p className="text-sm text-white/45 text-center py-6">No items yet — click “Add item” to create one.</p>
          ) : (
            <div className="space-y-4">
              {items.map((it, idx) => (
                <ListItemCard
                  key={it.id || idx}
                  index={idx}
                  total={items.length}
                  item={it}
                  list={list}
                  onChange={(k, v) => updateItem(idx, k, v)}
                  onRemove={() => removeItem(idx)}
                  onMove={(dir) => moveItem(idx, dir)}
                  onToggle={list.hasItemToggle ? () => toggleItem(idx) : null}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------ Subcomponents ------------------ */

function FieldInput({ field, value, onChange }) {
  if (field.type === "textarea") {
    return (
      <div>
        <label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">{field.label}</label>
        <textarea
          rows={3}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`field-${field.key}`}
          className="input-dark resize-none"
        />
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div>
        <label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">{field.label}</label>
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`field-${field.key}`}
          className="input-dark cursor-pointer"
        >
          {field.options.map((o) => (
            <option key={o} value={o} className="bg-[#1A1A1A]">{o}</option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === "image") {
    return (
      <div>
        <label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">{field.label}</label>
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <div className="w-full sm:w-48 h-32 rounded-xl overflow-hidden bg-[#0A0A0A] border border-white/10 shrink-0 flex items-center justify-center">
            {value ? (
              <img src={value} alt="preview" className="w-full h-full object-cover" />
            ) : (
              <ImageIcon className="w-8 h-8 text-white/20" />
            )}
          </div>
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://…"
            data-testid={`field-${field.key}`}
            className="input-dark flex-1"
          />
        </div>
      </div>
    );
  }

  if (field.type === "csv") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div>
        <label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">{field.label}</label>
        <textarea
          rows={2}
          value={arr.join(", ")}
          onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
          data-testid={`field-${field.key}`}
          className="input-dark resize-none"
          placeholder="Item 1, Item 2, Item 3"
        />
        <p className="mt-1.5 text-[11px] text-white/40">Separate items with commas.</p>
      </div>
    );
  }

  // text default
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">{field.label}</label>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`field-${field.key}`}
        className="input-dark"
      />
    </div>
  );
}

function ListItemCard({ index, total, item, list, onChange, onRemove, onMove, onToggle }) {
  const [open, setOpen] = React.useState(true);
  const enabled = item.enabled !== false;

  return (
    <div
      data-testid={`list-item-${index}`}
      className={`rounded-2xl border ${enabled ? "border-white/10" : "border-white/5 opacity-60"} bg-white/[0.02] overflow-hidden`}
    >
      <div className="flex items-center justify-between p-4 sm:p-5 gap-3">
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-3 min-w-0 flex-1 text-left"
        >
          {item.icon && <Icon name={item.icon} className="w-5 h-5 text-[#D4AF37] shrink-0" />}
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-white/40">#{index + 1}</div>
            <div className="font-display font-bold text-sm sm:text-base truncate">
              {list.itemLabel(item) || "Untitled"}
            </div>
          </div>
        </button>

        <div className="flex items-center gap-1 shrink-0">
          {onToggle && (
            <button
              onClick={onToggle}
              data-testid={`item-${index}-toggle`}
              title={enabled ? "Hide" : "Show"}
              className="p-2 rounded-lg text-white/50 hover:text-[#D4AF37] hover:bg-white/5 transition-all"
            >
              {enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={() => onMove(-1)}
            disabled={index === 0}
            title="Move up"
            className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/5 disabled:opacity-25 transition-all"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          <button
            onClick={() => onMove(1)}
            disabled={index === total - 1}
            title="Move down"
            className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/5 disabled:opacity-25 transition-all"
          >
            <ArrowDown className="w-4 h-4" />
          </button>
          <button
            onClick={onRemove}
            data-testid={`item-${index}-delete`}
            title="Remove"
            className="p-2 rounded-lg text-white/50 hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-white/5 p-4 sm:p-5 space-y-4 bg-black/20">
          {list.fields.map((f) => (
            <FieldInput
              key={f.key}
              field={{ ...f, key: f.key }}
              value={item[f.key]}
              onChange={(v) => onChange(f.key, v)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
